import {
	type CircleLayerSpecification,
	type FillLayerSpecification,
	type GeoJSONSource,
	type HeatmapLayerSpecification,
	type LineLayerSpecification,
	type SymbolLayerSpecification,
	type VectorTileSource
} from 'maplibre-gl';
import type { TLayer as TOperationalFeature } from '@safesystem/geoserver-api';

export type Environment = 'production' | 'demo' | 'development' | 'prototype';
export type LoggingLevel = 'fatal' | 'error' | 'warning' | 'info' | 'debug' | 'trace';

export enum FetchStatus {
	IDLE = 'IDLE',
	LOADING = 'LOADING',
	SUCCESS = 'SUCCESS',
	FAILURE = 'FAILURE',
	EMPTY = 'EMPTY'
}

type BBox = {
	minx: number;
	miny: number;
	maxx: number;
	maxy: number;
};

export type TLayer =
	| CircleLayerSpecification
	| FillLayerSpecification
	| HeatmapLayerSpecification
	| LineLayerSpecification
	| SymbolLayerSpecification;
export interface ILayerSource {
	id: string;
	zIndex: number;
	title: string;
	bbox?: BBox;
	layer: Partial<TLayer>;
	source: (Partial<VectorTileSource> & { attribution: string }) | Partial<GeoJSONSource>;
	detail?: string;
	/** GeoServer abstract, shown by the layer's info buttons. */
	abstract?: string;
	// Excludes this layer from click-to-select and its hover pointer cursor,
	// while still rendering it and keeping it toggleable in the legend.
	// Defaults to true when omitted.
	selectable?: boolean;
}

// ----------------------------------------------------------------------------
// Generic geodata shapes — parameterised so each app fulfils them with its
// own product / module / layer-id enums. App specialisations live in
// `apps/<id>/interfaces.ts`.
// ----------------------------------------------------------------------------

/** Operational feature carrying its product + module annotations. */
export interface IFeature<
	TProduct extends string = string,
	TModule extends string = string
> extends TOperationalFeature {
	product: TProduct;
	module: TModule;
	detail?: string;
}

/**
 * `module → layerId → ILayerSource | null` — the assembled operational layer
 * envelope consumed by Map / Table / legends. Apps needing stricter
 * per-module-per-layer typing declare their own narrow interface that is
 * structurally a subtype of this generic (see SafeCurves' `ISystemLayers`).
 */
export type ISystemLayers<M extends string = string, L extends string = string> = Record<
	M,
	Partial<Record<L, ILayerSource | null>>
>;
